// src/services/cryptoService.js

import axios from 'axios';
import { getAllCoinGeckoIds } from './cryptoConfig';

/**
 * Cryptocurrency Service
 * Handles all API interactions with CoinGecko
 * FREE API - No key required for basic use
 * Rate limit: 50 calls/minute
 */

const COINGECKO_API_BASE = 'https://api.coingecko.com/api/v3';

// Cache for prices to avoid excessive API calls
let priceCache = {};
let lastFetchTime = 0;
const CACHE_DURATION = 60000; // 1 minute

/**
 * Get current price for a single cryptocurrency
 */
export const getCryptoPrice = async (coingeckoId) => {
  try {
    const response = await axios.get(
      `${COINGECKO_API_BASE}/simple/price`,
      {
        params: {
          ids: coingeckoId,
          vs_currencies: 'usd,gbp',
          include_24hr_change: true,
          include_market_cap: true,
          include_24hr_vol: true
        }
      }
    );

    return response.data[coingeckoId];
  } catch (error) {
    console.error('Error fetching crypto price:', error);
    throw error;
  }
};

/**
 * Get current prices for multiple cryptocurrencies (batch)
 * More efficient than individual calls
 */
export const getBatchCryptoPrices = async (coingeckoIds) => {
  // Check cache first
  const now = Date.now();
  if (priceCache && Object.keys(priceCache).length > 0 && (now - lastFetchTime) < CACHE_DURATION) {
    return priceCache;
  }

  try {
    const response = await axios.get(
      `${COINGECKO_API_BASE}/simple/price`,
      {
        params: {
          ids: coingeckoIds.join(','),
          vs_currencies: 'usd,gbp',
          include_24hr_change: true,
          include_market_cap: true,
          include_24hr_vol: true
        }
      }
    );

    // Update cache
    priceCache = response.data;
    lastFetchTime = now;

    return response.data;
  } catch (error) {
    console.error('Error fetching batch crypto prices:', error);
    
    // Return cached data if available
    if (Object.keys(priceCache).length > 0) {
      console.log('Returning cached prices due to API error');
      return priceCache;
    }
    
    throw error;
  }
};

/**
 * Get all supported crypto prices
 */
export const getAllCryptoPrices = async () => {
  const allIds = getAllCoinGeckoIds();
  return await getBatchCryptoPrices(allIds);
};

/**
 * Get detailed crypto information
 */
export const getCryptoDetails = async (coingeckoId) => {
  try {
    const response = await axios.get(
      `${COINGECKO_API_BASE}/coins/${coingeckoId}`,
      {
        params: {
          localization: false,
          tickers: false,
          community_data: true,
          developer_data: false,
          sparkline: false
        }
      }
    );

    return {
      id: response.data.id,
      symbol: response.data.symbol,
      name: response.data.name,
      image: response.data.image?.large,
      currentPrice: response.data.market_data?.current_price,
      marketCap: response.data.market_data?.market_cap,
      marketCapRank: response.data.market_cap_rank,
      totalVolume: response.data.market_data?.total_volume,
      high24h: response.data.market_data?.high_24h,
      low24h: response.data.market_data?.low_24h,
      priceChange24h: response.data.market_data?.price_change_24h,
      priceChangePercentage24h: response.data.market_data?.price_change_percentage_24h,
      circulatingSupply: response.data.market_data?.circulating_supply,
      totalSupply: response.data.market_data?.total_supply,
      maxSupply: response.data.market_data?.max_supply,
      ath: response.data.market_data?.ath,
      athDate: response.data.market_data?.ath_date,
      atl: response.data.market_data?.atl,
      atlDate: response.data.market_data?.atl_date,
      description: response.data.description?.en,
      homepage: response.data.links?.homepage?.[0],
      blockchain: response.data.links?.blockchain_site?.[0]
    };
  } catch (error) {
    console.error('Error fetching crypto details:', error);
    throw error;
  }
};

/**
 * Get historical price data for charts
 */
export const getCryptoHistoricalData = async (coingeckoId, days = 30, currency = 'usd') => {
  try {
    const response = await axios.get(
      `${COINGECKO_API_BASE}/coins/${coingeckoId}/market_chart`,
      {
        params: {
          vs_currency: currency,
          days: days,
          interval: days > 90 ? 'daily' : 'hourly'
        }
      }
    );

    return {
      prices: response.data.prices,
      marketCaps: response.data.market_caps,
      volumes: response.data.total_volumes
    };
  } catch (error) {
    console.error('Error fetching crypto historical data:', error);
    throw error;
  }
};

/**
 * Search for cryptocurrencies
 */
export const searchCrypto = async (query) => {
  try {
    const response = await axios.get(
      `${COINGECKO_API_BASE}/search`,
      {
        params: {
          query: query
        }
      }
    );

    return response.data.coins.slice(0, 10); // Return top 10 results
  } catch (error) {
    console.error('Error searching crypto:', error);
    throw error;
  }
};

/**
 * Get trending cryptocurrencies
 */
export const getTrendingCryptos = async () => {
  try {
    const response = await axios.get(
      `${COINGECKO_API_BASE}/search/trending`
    );

    return response.data.coins;
  } catch (error) {
    console.error('Error fetching trending cryptos:', error);
    throw error;
  }
};

/**
 * Convert between cryptocurrencies
 */
export const convertCrypto = async (fromCoin, toCoin, amount) => {
  try {
    // Get both prices in USD
    const prices = await getBatchCryptoPrices([fromCoin, toCoin]);
    
    const fromPrice = prices[fromCoin]?.usd;
    const toPrice = prices[toCoin]?.usd;
    
    if (!fromPrice || !toPrice) {
      throw new Error('Unable to fetch conversion rates');
    }
    
    const usdValue = amount * fromPrice;
    const convertedAmount = usdValue / toPrice;
    
    return {
      from: {
        coin: fromCoin,
        amount: amount,
        usdValue: usdValue
      },
      to: {
        coin: toCoin,
        amount: convertedAmount,
        usdValue: usdValue
      },
      rate: fromPrice / toPrice
    };
  } catch (error) {
    console.error('Error converting crypto:', error);
    throw error;
  }
};

/**
 * Get global crypto market data
 */
export const getGlobalMarketData = async () => {
  try {
    const response = await axios.get(
      `${COINGECKO_API_BASE}/global`
    );

    return {
      totalMarketCap: response.data.data.total_market_cap,
      totalVolume: response.data.data.total_volume,
      marketCapPercentage: response.data.data.market_cap_percentage,
      marketCapChangePercentage24h: response.data.data.market_cap_change_percentage_24h_usd,
      activeCryptocurrencies: response.data.data.active_cryptocurrencies,
      markets: response.data.data.markets,
      btcDominance: response.data.data.market_cap_percentage.btc
    };
  } catch (error) {
    console.error('Error fetching global market data:', error);
    throw error;
  }
};

/**
 * Clear price cache (useful for forcing refresh)
 */
export const clearPriceCache = () => {
  priceCache = {};
  lastFetchTime = 0;
};

/**
 * Helper function to handle rate limiting
 * Implements exponential backoff
 */
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export const fetchWithRetry = async (fetchFunction, maxRetries = 3) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fetchFunction();
    } catch (error) {
      if (error.response?.status === 429 && i < maxRetries - 1) {
        // Rate limited, wait and retry
        const waitTime = Math.pow(2, i) * 1000; // Exponential backoff
        console.log(`Rate limited, retrying in ${waitTime}ms...`);
        await delay(waitTime);
      } else {
        throw error;
      }
    }
  }
};

export default {
  getCryptoPrice,
  getBatchCryptoPrices,
  getAllCryptoPrices,
  getCryptoDetails,
  getCryptoHistoricalData,
  searchCrypto,
  getTrendingCryptos,
  convertCrypto,
  getGlobalMarketData,
  clearPriceCache,
  fetchWithRetry
};
